import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/auth.store';
import {
  AnalyticsService,
  DashboardOverview,
  OverviewRange,
  PeriodFigure,
  StatusCount,
} from '../../services/analytics.service';

const RANGE_OPTIONS: { value: OverviewRange; label: string; comparison: string }[] = [
  { value: '30d', label: '30 days', comparison: 'vs previous 30 days' },
  { value: '90d', label: '90 days', comparison: 'vs previous 90 days' },
  { value: '12m', label: '12 months', comparison: 'vs previous 12 months' },
  { value: 'all', label: 'All time', comparison: '' },
];

// Validated as a colour-blind-safe pair against the white card surface
const SERIES = {
  sales: { name: 'Sales', color: '#4f46e5' },
  purchases: { name: 'Purchases', color: '#0d9488' },
};

const STATUS_STYLES: Record<string, { label: string; color: string }> = {
  quote: { label: 'Quote', color: 'text-gray-700 bg-gray-50 ring-gray-500/20' },
  draft: { label: 'Draft', color: 'text-gray-700 bg-gray-50 ring-gray-500/20' },
  order: { label: 'Order', color: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  planned: { label: 'Planned', color: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  approved: { label: 'Approved', color: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  confirmed: { label: 'Confirmed', color: 'text-indigo-700 bg-indigo-50 ring-indigo-600/20' },
  in_progress: { label: 'In Progress', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
  on_hold: { label: 'On Hold', color: 'text-orange-700 bg-orange-50 ring-orange-600/20' },
  shipped: { label: 'Shipped', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
  delivered: { label: 'Delivered', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  received: { label: 'Received', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  completed: { label: 'Completed', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 ring-red-600/20' },
};

const FALLBACK_BADGE = 'text-gray-700 bg-gray-50 ring-gray-500/20';
const badgeClass = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset';
const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';

const formatNumber = (value: number) => Number(value || 0).toLocaleString('en-IN');

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const pluralize = (count: number, singular: string, plural: string) =>
  `${formatNumber(count)} ${count === 1 ? singular : plural}`;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatMonth = (month: string) => {
  const [year, monthIndex] = month.split('-').map(Number);
  return new Date(year, monthIndex - 1, 1).toLocaleDateString('en-IN', {
    month: 'short',
    year: '2-digit',
  });
};

const statusLabel = (status: string) =>
  STATUS_STYLES[status]?.label || status.replace(/_/g, ' ');

const getDisplayName = (user: any) => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return user?.name || fullName || user?.username || '';
};

const ChangeIndicator: React.FC<{
  figure: PeriodFigure;
  comparison: string;
  // Rising sales are good news; rising spend is neither good nor bad on its own
  positiveIsGood: boolean;
}> = ({ figure, comparison, positiveIsGood }) => {
  if (!comparison) {
    return <p className="mt-1 text-sm text-gray-500">{pluralize(figure.orders, 'order', 'orders')} in total</p>;
  }

  if (figure.changePercent === null) {
    return (
      <p className="mt-1 text-sm text-gray-500">
        {pluralize(figure.orders, 'order', 'orders')} · no earlier period to compare
      </p>
    );
  }

  const isUp = figure.changePercent >= 0;
  const Icon = isUp ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  const tone = !positiveIsGood ? 'text-gray-700' : isUp ? 'text-green-700' : 'text-red-700';

  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-gray-500">
      <span className={`inline-flex items-center font-medium ${tone}`}>
        <Icon className="mr-1 h-4 w-4" aria-hidden="true" />
        {isUp ? '+' : ''}
        {figure.changePercent.toFixed(1)}%
      </span>
      {comparison}
    </p>
  );
};

const TrendTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey} className="mt-1 flex items-center text-sm text-gray-700">
          <span
            className="mr-2 inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}: <span className="ml-1 font-medium text-gray-900">{formatCurrency(entry.value)}</span>
        </p>
      ))}
    </div>
  );
};

const StatusBreakdown: React.FC<{
  title: string;
  rows: StatusCount[];
  to: string;
  emptyText: string;
}> = ({ title, rows, to, emptyText }) => {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const sorted = [...rows].sort((a, b) => b.count - a.count);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">
          {title} <span className="font-normal text-gray-500">({formatNumber(total)})</span>
        </h3>
        <Link to={to} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
          View
        </Link>
      </div>
      {sorted.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{emptyText}</p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-2">
          {sorted.map((row) => (
            <li
              key={row.status}
              className={`${badgeClass} ${STATUS_STYLES[row.status]?.color || FALLBACK_BADGE} capitalize`}
            >
              {statusLabel(row.status)} · {formatNumber(row.count)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const RankedList: React.FC<{
  title: string;
  description: string;
  rows: { key: string; name: string; detail: string; value: number }[];
  barClass: string;
  emptyText: string;
}> = ({ title, description, rows, barClass, emptyText }) => {
  const maxValue = Math.max(...rows.map((row) => row.value), 0);

  return (
    <div className={`${cardClass} p-5`}>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {rows.length === 0 ? (
        <p className="mt-6 py-6 text-center text-sm text-gray-500">{emptyText}</p>
      ) : (
        <ol className="mt-4 space-y-4">
          {rows.map((row, index) => (
            <li key={row.key}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate font-medium text-gray-900">
                  <span className="mr-2 text-gray-400 tabular-nums">{index + 1}</span>
                  {row.name}
                </span>
                <span className="shrink-0 font-medium text-gray-900 tabular-nums">
                  {formatCurrency(row.value)}
                </span>
              </div>
              <p className="mt-0.5 pl-5 text-xs text-gray-500">{row.detail}</p>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100">
                <div
                  className={`h-1.5 rounded-full ${barClass}`}
                  style={{ width: `${maxValue > 0 ? (row.value / maxValue) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const DashboardSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-6" aria-hidden="true">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className={`${cardClass} h-28`} />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div className={`${cardClass} h-96 xl:col-span-2`} />
      <div className={`${cardClass} h-96`} />
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [range, setRange] = useState<OverviewRange>('30d');

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['dashboardOverview', range],
    queryFn: () => AnalyticsService.getOverview(range),
    placeholderData: keepPreviousData,
  });

  const displayName = getDisplayName(user);
  const rangeOption = RANGE_OPTIONS.find((option) => option.value === range) || RANGE_OPTIONS[0];
  const periodLabel = range === 'all' ? 'all time' : `last ${rangeOption.label}`;

  const renderContent = (overview: DashboardOverview) => {
    const totalOpenOrders =
      overview.openOrders.sales + overview.openOrders.purchases + overview.openOrders.workOrders;

    const attentionItems = [
      { count: overview.attention.lowStock, singular: 'product at or below minimum stock', plural: 'products at or below minimum stock', to: '/inventory' },
      { count: overview.attention.purchasePendingApproval, singular: 'purchase order waiting for approval', plural: 'purchase orders waiting for approval', to: '/purchase' },
      { count: overview.attention.purchaseAwaitingReceipt, singular: 'approved purchase order not yet received', plural: 'approved purchase orders not yet received', to: '/purchase' },
      { count: overview.attention.purchaseOverdue, singular: 'purchase order past its expected date', plural: 'purchase orders past their expected date', to: '/purchase' },
      { count: overview.attention.salesOverdue, singular: 'sales order past its delivery date', plural: 'sales orders past their delivery date', to: '/sales' },
      { count: overview.attention.workOrdersOverdue, singular: 'work order past its planned end date', plural: 'work orders past their planned end date', to: '/manufacturing' },
    ].filter((item) => item.count > 0);

    const trendData = overview.monthlyTrend.map((row) => ({
      ...row,
      label: formatMonth(row.month),
    }));
    const hasTrendData = trendData.some((row) => row.sales > 0 || row.purchases > 0);

    const maxCategoryValue = Math.max(...overview.inventoryByCategory.map((row) => row.value), 0);

    return (
      <div className={`space-y-6 ${isFetching ? 'opacity-70' : ''}`}>
        {/* Key figures */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Sales revenue</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <BanknotesIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">
              {formatCurrency(overview.sales.value)}
            </p>
            <ChangeIndicator figure={overview.sales} comparison={rangeOption.comparison} positiveIsGood />
          </div>

          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Purchase spend</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                <ShoppingCartIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">
              {formatCurrency(overview.purchases.value)}
            </p>
            <ChangeIndicator
              figure={overview.purchases}
              comparison={rangeOption.comparison}
              positiveIsGood={false}
            />
          </div>

          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Inventory value (at cost)</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <CubeIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">
              {formatCurrency(overview.inventory.stockValue)}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {pluralize(overview.inventory.totalProducts, 'product', 'products')} ·{' '}
              {formatNumber(overview.inventory.lowStock)} low on stock
            </p>
          </div>

          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Open orders</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-yellow-50 text-yellow-600">
                <ClipboardDocumentListIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">
              {formatNumber(totalOpenOrders)}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {formatNumber(overview.openOrders.sales)} sales · {formatNumber(overview.openOrders.purchases)}{' '}
              purchase · {formatNumber(overview.openOrders.workOrders)} production
            </p>
          </div>
        </div>

        {/* Secondary figures */}
        <dl className={`${cardClass} grid grid-cols-2 divide-gray-200 lg:grid-cols-4 lg:divide-x`}>
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500">Sales minus purchases</dt>
            <dd
              className={`mt-1 text-xl font-semibold tabular-nums ${
                overview.netPosition < 0 ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {formatCurrency(overview.netPosition)}
            </dd>
            <dd className="mt-1 text-sm text-gray-500">For {periodLabel}</dd>
          </div>
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500">Average sales order</dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
              {formatCurrency(overview.averageSalesOrderValue)}
            </dd>
            <dd className="mt-1 text-sm text-gray-500">
              Across {formatNumber(overview.sales.orders)} {overview.sales.orders === 1 ? 'order' : 'orders'}
            </dd>
          </div>
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500">Customers and vendors</dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
              {formatNumber(overview.parties.activeCustomers)}
              <span className="text-gray-400"> / </span>
              {formatNumber(overview.parties.activeVendors)}
            </dd>
            <dd className="mt-1 text-sm text-gray-500">
              <Link to="/customers" className="font-medium text-indigo-600 hover:text-indigo-500">
                Active customers
              </Link>{' '}
              /{' '}
              <Link to="/vendors" className="font-medium text-indigo-600 hover:text-indigo-500">
                vendors
              </Link>
            </dd>
          </div>
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500">Unpaid invoices</dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
              {formatCurrency(overview.invoices.unpaidValue)}
            </dd>
            <dd className="mt-1 text-sm text-gray-500">
              {formatNumber(overview.invoices.unpaidCount)} open
              {overview.invoices.overdueCount > 0 && (
                <span className="font-medium text-red-600">
                  {' '}· {formatNumber(overview.invoices.overdueCount)} overdue
                </span>
              )}
            </dd>
          </div>
        </dl>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Sales vs purchases */}
          <div className={`${cardClass} p-5 xl:col-span-2`}>
            <h2 className="text-base font-semibold text-gray-900">Sales and purchases by month</h2>
            <p className="mt-1 text-sm text-gray-500">
              Last 12 months, by order date. Quotes and cancelled orders are excluded.
            </p>
            {hasTrendData ? (
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCompactCurrency}
                      width={64}
                    />
                    <Tooltip content={<TrendTooltip />} cursor={{ fill: '#f9fafb' }} />
                    <Legend
                      iconType="square"
                      formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
                    />
                    <Bar
                      dataKey="sales"
                      name={SERIES.sales.name}
                      fill={SERIES.sales.color}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                    <Bar
                      dataKey="purchases"
                      name={SERIES.purchases.name}
                      fill={SERIES.purchases.color}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="mt-4 flex h-72 flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 text-center">
                <BanknotesIcon className="h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm font-medium text-gray-900">No orders in the last 12 months</p>
                <p className="mt-1 text-sm text-gray-500">
                  Sales and purchase totals will appear here as orders are placed.
                </p>
              </div>
            )}
          </div>

          {/* Needs attention */}
          <div className={`${cardClass} p-5`}>
            <h2 className="text-base font-semibold text-gray-900">Needs attention</h2>
            <p className="mt-1 text-sm text-gray-500">Open items across the business right now.</p>
            {attentionItems.length === 0 ? (
              <div className="mt-6 flex flex-col items-center py-8 text-center">
                <CheckCircleIcon className="h-10 w-10 text-green-500" />
                <p className="mt-2 text-sm font-medium text-gray-900">Nothing needs attention</p>
                <p className="mt-1 text-sm text-gray-500">No low stock, pending approvals or overdue orders.</p>
              </div>
            ) : (
              <ul className="mt-4 divide-y divide-gray-100">
                {attentionItems.map((item) => (
                  <li key={item.plural}>
                    <Link
                      to={item.to}
                      className="group flex items-center gap-3 rounded-md px-2 py-3 hover:bg-gray-50"
                    >
                      <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-yellow-500" aria-hidden="true" />
                      <span className="flex-1 text-sm text-gray-700">
                        <span className="font-semibold text-gray-900">{formatNumber(item.count)}</span>{' '}
                        {item.count === 1 ? item.singular : item.plural}
                      </span>
                      <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Top performers */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <RankedList
            title="Top products"
            description={`By sales revenue, ${periodLabel}.`}
            rows={overview.topProducts.map((row) => ({
              key: row.productId,
              name: row.name,
              detail: `${formatNumber(row.quantity)} sold`,
              value: row.revenue,
            }))}
            barClass="bg-indigo-500"
            emptyText="No product sales in this period."
          />
          <RankedList
            title="Top customers"
            description={`By sales value, ${periodLabel}.`}
            rows={overview.topCustomers.map((row) => ({
              key: row.id,
              name: row.name,
              detail: `${formatNumber(row.orders)} ${row.orders === 1 ? 'order' : 'orders'}`,
              value: row.value,
            }))}
            barClass="bg-indigo-500"
            emptyText="No customer orders in this period."
          />
          <RankedList
            title="Top vendors"
            description={`By purchase spend, ${periodLabel}.`}
            rows={overview.topVendors.map((row) => ({
              key: row.id,
              name: row.name,
              detail: `${formatNumber(row.orders)} ${row.orders === 1 ? 'order' : 'orders'}`,
              value: row.value,
            }))}
            barClass="bg-teal-500"
            emptyText="No vendor purchases in this period."
          />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Orders by status */}
          <div className={`${cardClass} p-5`}>
            <h2 className="text-base font-semibold text-gray-900">Orders by status</h2>
            <p className="mt-1 text-sm text-gray-500">All orders on record.</p>
            <div className="mt-4 space-y-5">
              <StatusBreakdown title="Sales orders" rows={overview.salesByStatus} to="/sales" emptyText="No sales orders yet." />
              <StatusBreakdown title="Purchase orders" rows={overview.purchasesByStatus} to="/purchase" emptyText="No purchase orders yet." />
              <StatusBreakdown title="Work orders" rows={overview.workOrdersByStatus} to="/manufacturing" emptyText="No work orders yet." />
            </div>
          </div>

          {/* Inventory by category */}
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Inventory value by category</h2>
              <Link to="/inventory" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                View
              </Link>
            </div>
            <p className="mt-1 text-sm text-gray-500">Stock on hand valued at cost price.</p>
            {overview.inventoryByCategory.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">No products yet.</p>
            ) : (
              <ul className="mt-4 space-y-4">
                {overview.inventoryByCategory.map((row) => (
                  <li key={row.category}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate font-medium text-gray-900">
                        {row.category}{' '}
                        <span className="font-normal text-gray-500">
                          · {formatNumber(row.items)} {row.items === 1 ? 'item' : 'items'}
                        </span>
                      </span>
                      <span className="font-medium text-gray-900 tabular-nums">{formatCurrency(row.value)}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full rounded-full bg-gray-100">
                      <div
                        className="h-2 rounded-full bg-indigo-500"
                        style={{ width: `${maxCategoryValue > 0 ? (row.value / maxCategoryValue) * 100 : 0}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Low stock */}
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Low stock</h2>
              <Link to="/inventory" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                View
              </Link>
            </div>
            <p className="mt-1 text-sm text-gray-500">Products at or below their minimum level.</p>
            <dl className="mt-4 grid grid-cols-4 gap-2 text-center">
              {[
                { label: 'Healthy', value: overview.inventory.healthy, tone: 'text-green-700' },
                { label: 'Low', value: overview.inventory.lowStock, tone: 'text-red-600' },
                { label: 'Out', value: overview.inventory.outOfStock, tone: 'text-red-600' },
                { label: 'Over max', value: overview.inventory.overstocked, tone: 'text-yellow-700' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-md bg-gray-50 px-2 py-2">
                  <dd className={`text-lg font-semibold tabular-nums ${stat.value > 0 ? stat.tone : 'text-gray-900'}`}>
                    {formatNumber(stat.value)}
                  </dd>
                  <dt className="text-xs text-gray-500">{stat.label}</dt>
                </div>
              ))}
            </dl>
            {overview.lowStockItems.length === 0 ? (
              <div className="mt-4 flex flex-col items-center py-4 text-center">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
                <p className="mt-2 text-sm font-medium text-gray-900">No product is below its minimum</p>
              </div>
            ) : (
              <ul className="mt-4 divide-y divide-gray-100">
                {overview.lowStockItems.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-500">{item.sku}</p>
                    </div>
                    <p className="shrink-0 text-right text-sm">
                      <span className="font-semibold text-red-600 tabular-nums">
                        {formatNumber(item.currentStock)}
                      </span>
                      <span className="text-gray-500">
                        {' '}/ min {formatNumber(item.minimumStock)} {item.unit}
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Recent orders */}
        <div className={`${cardClass} overflow-hidden xl:col-span-2`}>
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-900">Recent orders</h2>
            <p className="mt-1 text-sm text-gray-500">The latest sales and purchase orders.</p>
          </div>
          {overview.recentOrders.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-500">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Order', 'Type', 'Customer / Vendor', 'Order Date', 'Status'].map((heading) => (
                      <th
                        key={heading}
                        className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {heading}
                      </th>
                    ))}
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {overview.recentOrders.map((order) => (
                    <tr key={`${order.type}-${order.id}`} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-5 py-3 text-sm">
                        <Link
                          to={order.type === 'sales' ? '/sales' : '/purchase'}
                          className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 hover:bg-gray-200"
                        >
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">
                        {order.type === 'sales' ? 'Sales' : 'Purchase'}
                      </td>
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{order.party}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">
                        {formatDate(order.orderDate)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-sm">
                        <span className={`${badgeClass} ${STATUS_STYLES[order.status]?.color || FALLBACK_BADGE} capitalize`}>
                          {statusLabel(order.status)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                        {formatCurrency(order.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent stock movements */}
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Recent stock movements</h2>
            <Link to="/inventory" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              View
            </Link>
          </div>
          <p className="mt-1 text-sm text-gray-500">The latest stock received, issued or adjusted.</p>
          {overview.recentStockMovements.length === 0 ? (
            <p className="mt-6 py-6 text-center text-sm text-gray-500">No stock movements yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100">
              {overview.recentStockMovements.map((movement) => {
                const MovementIcon =
                  movement.type === 'in' ? ArrowUpIcon : movement.type === 'out' ? ArrowDownIcon : ArrowsRightLeftIcon;
                const sign = movement.type === 'in' ? '+' : movement.type === 'out' ? '−' : '';
                return (
                  <li key={movement.id} className="flex items-center gap-3 py-3">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        movement.type === 'in'
                          ? 'bg-green-50 text-green-600'
                          : movement.type === 'out'
                            ? 'bg-red-50 text-red-600'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <MovementIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{movement.productName}</p>
                      <p className="text-xs text-gray-500">
                        <span className="capitalize">{movement.reason.replace(/_/g, ' ')}</span> ·{' '}
                        {formatDateTime(movement.createdAt)}
                      </p>
                    </div>
                    <p className="shrink-0 text-right text-sm">
                      <span className="font-semibold text-gray-900 tabular-nums">
                        {sign}
                        {formatNumber(movement.quantity)}
                      </span>
                      <span className="block text-xs text-gray-500">
                        now {formatNumber(movement.newStock)}
                      </span>
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            {displayName ? `Welcome back, ${displayName}. ` : 'Welcome back. '}
            Sales and purchase figures cover {periodLabel}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Reporting period">
            {RANGE_OPTIONS.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setRange(option.value)}
                aria-pressed={range === option.value}
                className={`border px-3 py-2 text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  index === 0 ? 'rounded-l-md' : '-ml-px'
                } ${index === RANGE_OPTIONS.length - 1 ? 'rounded-r-md' : ''} ${
                  range === option.value
                    ? 'z-10 border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <DashboardSkeleton />
      ) : error || !data ? (
        <div className={`${cardClass} px-6 py-12 text-center`}>
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
          <p className="mt-2 text-sm font-medium text-gray-900">The dashboard could not be loaded</p>
          <p className="mt-1 text-sm text-gray-500">Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          {renderContent(data)}
          <p className="text-xs text-gray-500">
            Updated{' '}
            {formatDateTime(data.generatedAt)}
          </p>
        </>
      )}
    </div>
  );
};

export default Dashboard;
